/**  MODIFIED COPY OF src/common-app/state/modules/engines/voice-mask/services.ts TODO find way to reuse common code! */
import { modules } from '@veritone/glc-redux';

const { user } = modules;
import { TDOId } from '@common-modules/universal/models/Brands';
import { selectConfigEngines } from '../selectors';
import callGraphQLApi, {
  GQLApiDispatch,
  GQLApiGetState,
} from '@helpers/callGraphQLApi';
import { speakerSeparationJobInput } from '@helpers/speakerSeparationDag';
import { selectTranscription } from '@common-modules/mediaDetails/selectors';
import { languageOfTranscriptionEngine } from '@helpers/speakerSeparationLanguage';
import { DEFAULT_TRANSCRIPTION_LANGUAGE } from '../../../../../utils/speechmatics/engines';

import {
  NOOP,
  CREATE_ENGINE_JOB_SUCCESS,
  CREATE_ENGINE_JOB_FAILURE,
  CHECK_JOB_STATUS_FAILURE,
  CHECK_JOB_STATUS_SUCCESS,
  CHECK_TASK_STATUS_FAILURE,
  CHECK_TASK_STATUS_SUCCESS,
} from './actions';

export const createEngineJobService =
  ({
    tdoId,
    payload,
  }: {
    tdoId: TDOId;
    payload: {
      targetId: string;
      url: string;
      /** See the action creator: set when the transcript is not on screen. */
      transcriptionLanguage?: string;
    };
  }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const state = getState();
    const {
      speakerSeparationEngines,
      transcriptionEngines,
      defaultClusterId,
      glcIngestionEngineId,
      outputWriterEngineId,
      transcriptionCategoryId,
    } = selectConfigEngines(state);
    const email = user.selectUser(state).email;

    /**
     * Separate in the language the transcript was run in, not the default.
     *
     * Same reasoning as `transcriptEngineId` below: Speechmatics ships one
     * separation engine per language, and the audio this job is handed is the
     * audio that transcript came from. The language is not recorded on the
     * transcript, only the engine that produced it, so it comes back through
     * the per-language map, then through `RETIRED_TRANSCRIPTION_ENGINES` for
     * the ids we used to ship.
     *
     * The fallback is a GUESS, and a worse one than it used to be. It read
     * "an engine the map does not know ran before the map, so it ran in the
     * default language" — true when the map was the single English setup,
     * false now that the app ships es/fr: an unknown id can just as easily be
     * a Spanish transcript from an org that configured its own engine. The
     * cost is quiet — the English separation model drawing speaker boundaries
     * on Spanish audio returns a plausible, worse answer, with nothing saying
     * so. The retired table shrinks how often this fires; it does not make the
     * guess right. Worth replacing with the language of the run that produced
     * the transcript, once that is recorded (VE-18990).
     */
    const loadedTranscriptEngineId = selectTranscription(state)?.engineId;
    const requestedLanguage = payload.transcriptionLanguage;
    const language =
      requestedLanguage ??
      languageOfTranscriptionEngine(
        transcriptionEngines,
        loadedTranscriptEngineId
      ) ??
      DEFAULT_TRANSCRIPTION_LANGUAGE;
    /**
     * Which transcript this separation is for.
     *
     * Normally the one on screen. On a replace there is no such transcript:
     * the old one has been deleted and the new one is still being made, so
     * the engine that is ABOUT to produce it is the right answer — that is
     * what the requested language names. `transcriptCategoryId` below covers
     * it either way; this id is the narrower hint.
     */
    const transcriptEngineId = requestedLanguage
      ? (transcriptionEngines?.[requestedLanguage] ?? loadedTranscriptEngineId)
      : loadedTranscriptEngineId;
    const speakerSeparationEngineId = speakerSeparationEngines[language];

    if (!speakerSeparationEngineId) {
      throw new Error(
        `No speaker separation engine configured for language "${language}"`
      );
    }

    if (!glcIngestionEngineId) {
      throw new Error('GLC ingestion engine ID not configured');
    }

    const input = speakerSeparationJobInput({
      targetId: tdoId,
      clusterId: defaultClusterId,
      url: payload.url,
      glcIngestionEngineId,
      speakerSeparationEngineId,
      outputWriterEngineId,
      // The engine that actually produced the transcript on screen, not the
      // one currently configured: Redact picks a transcription engine per
      // language, so the configured id is the wrong answer for any transcript
      // run in another language or before a config change.
      transcriptEngineId,
      transcriptCategoryId: transcriptionCategoryId,
      user: email,
    });

    return await callGraphQLApi<{
      createJob: {
        id: string;
        targetId: TDOId;
        status: string;
      };
    }>({
      actionTypes: [NOOP, CREATE_ENGINE_JOB_SUCCESS, CREATE_ENGINE_JOB_FAILURE],
      query: `mutation($input: CreateJob!) {
        createJob(input: $input) {
          id
          targetId
          status
        }
      }`,
      variables: { input },
      dispatch,
      getState,
    });
  };

/**
 * Get the status of a job.
 */
export const checkJobStatusService =
  ({ jobId }: { jobId: string }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const query = `query($jobId: ID!) {
      job(id: $jobId) {
        id
        targetId
        status
      }
    }`;

    return await callGraphQLApi<{
      job: {
        id: string;
        targetId: TDOId;
        status: string;
      };
    }>({
      actionTypes: [NOOP, CHECK_JOB_STATUS_SUCCESS, CHECK_JOB_STATUS_FAILURE],
      query,
      variables: { jobId },
      dispatch,
      getState,
    });
  };

/**
 * Get the status of one task — the separation run's own status, independent of
 * whatever else shares its job. See CHECK_TASK_STATUS.
 */
export const checkTaskStatusService =
  ({ taskId }: { taskId: string }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const query = `query($taskId: ID!) {
      task(id: $taskId) {
        id
        jobId
        targetId
        status
      }
    }`;

    return await callGraphQLApi<{
      task: {
        id: string;
        jobId: string;
        targetId: TDOId;
        status: string;
      };
    }>({
      actionTypes: [NOOP, CHECK_TASK_STATUS_SUCCESS, CHECK_TASK_STATUS_FAILURE],
      query,
      variables: { taskId },
      dispatch,
      getState,
    });
  };
